"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow2Icon, StartIcon, StopSign } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Styled Components ---

const Section = styled.section`
  padding: 40px 0;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 0;
  }
`

const SliderArea = styled.div`
  position: relative;
  width: 100%;

  /* controls are positioned relative to this wrapper, not
     SliderContainer which has overflow: hidden */
`

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 227px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 608px;
  }

  .swiper {
    padding-block: 0;
  }

  .swiper,
  .swiper-slide {
    height: 100%;
  }

  div.swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
  }

  div.swiper-slide.swiper-slide-active,
  div.swiper-slide.swiper-slide-duplicate-active {
    opacity: 1 !important;
  }
`

const SlideImage = styled(Image)`
  object-fit: cover;
`

const EdgeArrow = styled.button<{ side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: var(--comp-bg);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 58px;
  padding: 0;

  ${({ side }) =>
    side === "left"
      ? "left: 0; border-radius: 0 50px 50px 0;"
      : "right: 0; border-radius: 50px 0 0 50px;"}

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: -2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 56px;
    height: 67px;

    svg {
      width: 28px;
      height: 28px;
    }
  }
`

const BottomControls = styled.div`
  position: absolute;
  bottom: 20px;
  inset-inline: 25px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;

  /* The two ends are deliberately not symmetric (ASH-2135). The progress bars
     keep the page gutter, but Figma tucks the play/pause button in tight to the
     corner. inset-inline-end is the left in RTL, which is the side the button
     sits on because it is the last flex child under space-between. */
  ${(props) => props.theme.breakpoints.up("md")} {
    bottom: 40px;
    inset-inline-start: 120px;
    inset-inline-end: 40px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    inset-inline-start: 240px;
  }
`

const AutoplayBtn = styled.button`
  background: rgba(255, 255, 255, 1);
  opacity: 0.4;
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const ProgressBars = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const tabFillAnimation = `
  @keyframes gallery3TabFill {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }
`

const AUTOPLAY_DELAY = 3000

const Bar = styled.button<{
  active: boolean
  filling: boolean
  past: boolean
}>`
  ${tabFillAnimation}
  width: 24px;
  height: 3px;
  padding: 0;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  background-color: color-mix(in srgb, var(--comp-text) 100%, transparent);
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--comp-text);
    transform-origin: left;
    transform: ${({ past }) => (past ? "scaleX(1)" : "scaleX(0)")};
    animation: ${({ filling }) =>
      filling ? `gallery3TabFill ${AUTOPLAY_DELAY}ms linear forwards` : "none"};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 32px;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Main Component ---

function Gallery3({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [fillKey, setFillKey] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const images = (extractProperty(data, "images", true) as any[] | undefined) || []
  const slides = images.filter((img) => img?.url)
  const hasMultiple = slides.length > 1

  const togglePlay = () => {
    if (!swiperRef) return
    if (isPlaying) swiperRef.autoplay?.stop()
    else swiperRef.autoplay?.start()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.gallery)
    setIsPlaying(!isPlaying)
  }

  // Swiper fade+loop: manage pointer-events and accessibility on slides
  useEffect(() => {
    if (!containerRef.current || !hasMultiple) return
    const slideEls = containerRef.current.querySelectorAll(".swiper-slide")
    slideEls.forEach((slide) => {
      const el = slide as HTMLElement
      const isActive =
        el.classList.contains("swiper-slide-active") ||
        el.classList.contains("swiper-slide-duplicate-active")
      if (isActive) {
        el.removeAttribute("inert")
        el.removeAttribute("aria-hidden")
        el.style.pointerEvents = "auto"
        el.style.zIndex = "2"
      } else {
        el.setAttribute("inert", "")
        el.setAttribute("aria-hidden", "true")
        el.style.pointerEvents = "none"
        el.style.zIndex = "0"
      }
    })
  }, [activeIndex, hasMultiple])

  if (slides.length === 0) return null

  return (
    <Section>
      <SliderArea>
        <SliderContainer ref={containerRef}>
          {slides.length === 1 ? (
            <SlideImage
              src={getUmbracoMediaUrl(slides[0].url)}
              fill
              alt={slides[0].altText || ""}
              sizes="(max-width: 768px) 100vw, 1200px"
              priority
            />
          ) : (
            <>
              <Swiper
                modules={[EffectFade, Autoplay]}
                effect="fade"
                fadeEffect={{ crossFade: true }}
                onSwiper={setSwiperRef}
                onSlideChange={(swiper) => {
                  setActiveIndex(swiper.realIndex)
                  setFillKey((k) => k + 1)
                }}
                slidesPerView={1}
                loop
                autoplay={{ delay: 3000, disableOnInteraction: false }}
              >
                {slides.map((img, i) => (
                  <SwiperSlide key={i}>
                    <SlideImage
                      src={getUmbracoMediaUrl(img.url)}
                      fill
                      alt={img.altText || ""}
                      sizes="(max-width: 768px) 100vw, 1200px"
                      priority={i === 0}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </>
          )}
        </SliderContainer>

        {hasMultiple && (
          <>
            <EdgeArrow
              side="left"
              onClick={() => swiperRef?.slidePrev()}
              aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
            >
              <LeftArrow2Icon color="white" style={isRtl ? {} : { transform: "rotate(180deg)" }} />
            </EdgeArrow>
            <EdgeArrow
              side="right"
              onClick={() => swiperRef?.slideNext()}
              aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
            >
              <LeftArrow2Icon color="white" style={isRtl ? { transform: "rotate(180deg)" } : {}} />
            </EdgeArrow>

            <BottomControls>
              <ProgressBars>
                {slides.map((_, i) => (
                  <Bar
                    key={`${i}-${fillKey}`}
                    active={i === activeIndex}
                    filling={i === activeIndex && isPlaying}
                    past={i < activeIndex}
                    onClick={() => swiperRef?.slideToLoop(i)}
                    aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                    aria-current={i === activeIndex ? "true" : undefined}
                  />
                ))}
              </ProgressBars>
              <AutoplayBtn
                onClick={togglePlay}
                aria-label={
                  isPlaying
                    ? isRtl
                      ? "עצור תנועת הרכיב"
                      : "Stop animation"
                    : isRtl
                    ? "הפעל תנועה רכיב"
                    : "Play animation"
                }
              >
                {isPlaying ? <StopSign fill="#000" /> : <StartIcon fill="#000" />}
              </AutoplayBtn>
            </BottomControls>
          </>
        )}
      </SliderArea>
    </Section>
  )
}

export default Gallery3
